// src/components/character/PrintableCharacterSheet.jsx
import React, { useMemo } from "react";

function mapById(list, pickId) {
    const m = new Map();
    (list || []).forEach((x) => {
        const id =
            pickId?.(x) ??
            x?.raceId ??
            x?.skillsetId ??
            x?.skillId ??
            x?.id;
        if (id != null) m.set(id, x);
    });
    return m;
}


function Field({ label, value }) {
    const isZero = value === 0 || (typeof value === "string" && value.trim() === "0");
    return (
        <div className="characterform-group">
            <div className="characterform-label">{label}</div>
            <div className={`characterform-value${isZero ? " is-zero" : ""}`}>
                {value ?? ""}
            </div>
        </div>
    );
}


export default function PrintableCharacterSheet({
                                                    character,
                                                    races,
                                                    skillsets,
                                                    availableSkills,
                                                }) {
    const raceById = useMemo(() => mapById(races, (r) => r.raceId), [races]);
    const skillsetById = useMemo(
        () => mapById(skillsets, (s) => s.skillsetId),
        [skillsets]
    );
    const skillById = useMemo(
        () => mapById(availableSkills, (s) => s.skillId),
        [availableSkills]
    );

    const raceName =
        character?.raceId != null ? raceById.get(character.raceId)?.name || "" : "";
    const primaryName =
        character?.skillsetPrimary != null
            ? skillsetById.get(character.skillsetPrimary)?.name || ""
            : "";
    const secondaryName =
        character?.skillsetSecondary != null
            ? skillsetById.get(character.skillsetSecondary)?.name || ""
            : "";

    // ---- NEW: grouping exactly like SkillList ----
    const localSkillsetMap = useMemo(() => {
        const m = {};
        (skillsets || []).forEach((s) => (m[String(s.skillsetId)] = s.name));
        m.static = "Static";
        return m;
    }, [skillsets]);

    const labelForGroup = (groupKey) =>
        groupKey === "transform"
            ? "Transform Abilities"
            : localSkillsetMap?.[groupKey] || "Unknown";

    const grouped = useMemo(() => {
        const skillsArray = Array.isArray(character?.skills) ? character.skills : [];
        const out = skillsArray.reduce((acc, entry) => {
            const base = skillById.get(entry.skillId) || {};

            // Determine group key (match SkillList)
            let groupKey;
            if (base && base.primaryBaseCost === 0) groupKey = "static";
            else if (base && base.skillset !== undefined) groupKey = String(base.skillset);
            else if (base && base.source === "transform") groupKey = "transform";
            else groupKey = "unknown";

            (acc[groupKey] ||= []).push({ ...base, ...entry });
            return acc;
        }, {});

        // Sort groups: 'static' last, otherwise by label (case-insensitive)
        const keys = Object.keys(out).sort((a, b) => {
            // Static always first
            if (a === "static" && b !== "static") return -1;
            if (b === "static" && a !== "static") return 1;

            // Transform always last
            if (a === "transform" && b !== "transform") return 1;
            if (b === "transform" && a !== "transform") return -1;
            const A = labelForGroup(a).toUpperCase();
            const B = labelForGroup(b).toUpperCase();
            return A.localeCompare(B);
        });

        // Sort skills within each group by name
        return keys.map((k) => ({
            key: k,
            label: labelForGroup(k),
            items: out[k].slice().sort((s1, s2) => (s1.name || "").localeCompare(s2.name || "")),
        }));
    }, [character?.skills, skillById, localSkillsetMap]);

    const getSkillRate = (skill) => {
        const base = Number(skill?.baseCostUsed ?? 0);
        const inc  = Number(skill?.incrementCostUsed ?? 0);
        if (base === 0) return "-";
        if (inc === 0)  return String(base);
        return `${base}+${inc}`;
    }

    const getSPCostString = (skill) => {
        if (skill.cost > 0) {
            if (skill.incrementCost > 0) {
                return `${skill.cost} SP (${getSkillRate(skill)})`;
            }
            return `${skill.cost} SP`;
        }
        return "-";
    }

    const allSkills = character.skills?.map(s => skillById.get(s.skillId)) || [];

    // Only include skills that actually have a non-empty description
    const refSkills = useMemo(() => {
        return (allSkills || [])
            .filter(Boolean)
            .filter(s => {
                const desc = typeof s?.description === "string" ? formatRefText(s.description).trim() : "";
                return desc.length > 0;
            });
    }, [allSkills]);


    // Convert simple HTML breaks/paragraphs to newlines; strip other tags.
    function formatRefText(raw) {
        let t = raw || "";
        t = t.replace(/<br\s*\/?>/gi, "\n");
        t = t.replace(/<\/p>\s*<p>/gi, "\n");
        t = t.replace(/<\/?p\s*>/gi, "");     // remove remaining <p> wrappers
        t = t.replace(/<\/?[^>]+>/g, "");     // strip any other tags
        return t;
    }

    return (
        <div className="pcs-page theme-print">
            {/* Top: CharacterForm-like block */}
            {/* Page header (single line) */}
            <div className="pcs-page-header">
                <div className="pcs-head-left">
                    <span className="pcs-head-version">v5.1.0-1</span>
                    <span className="pcs-head-title">Chronicles Character Sheet</span>
                </div>

                <div className="pcs-head-fillers">
                    <span className="pcs-head-blank is-date">Event Date</span>
                    <span className="pcs-head-blank is-fee">Fee/Meal</span>
                    <span className="pcs-head-blank is-approval">Approval</span>
                </div>
            </div>

            <section className="pcs-section">
                <div className="characterform-form">
                    <div className="characterform-desktop-top">
                        <div className="characterform-row characterform-row-inline">
                            <Field label="Name" value={character?.name} />
                            <Field label="Player" value={character?.playerName} />
                        </div>
                        <div className="characterform-row characterform-row-inline">
                            <Field label="Race" value={raceName} />
                            <Field label="Sub Race" value={character?.subRace} />
                            <Field label="Skill Points" value={character?.skillPointsTotal} />
                            <Field label="SP Debt" value={character?.skillPointsDebt} />
                            <Field label="Effective SP" value={character?.skillPointsEffective} />
                            <Field label="Unspent SP" value={character?.skillPointsUnspent} />
                            <Field label="Bank" value={character?.bankBalance ?? 0} />
                        </div>
                    </div>

                    <div className="characterform-row characterform-row-inline">
                        <Field label="Primary" value={primaryName} />
                        <Field label="Secondary" value={secondaryName} />
                        <Field label="Transform" value={character?.transformName || ""} />
                    </div>

                    <div className="characterform-row characterform-row-inline">
                        <Field label="Agility" value={character?.agilityPoints} />
                        <Field label="Arcane" value={character?.arcanePoints} />
                        <Field label="Nature" value={character?.naturePoints} />
                        <Field label="Spirit" value={character?.spiritPoints} />
                        <Field label="Stamina" value={character?.staminaPoints} />
                        <Field label="Thaum." value={character?.thaumaturgyPoints} />
                    </div>

                    <div className="characterform-row characterform-row-invert characterform-row-inline">
                        <Field label="Body" value={character?.bodyPoints} />
                        <Field label="Armor Cap" value={character?.maxArmor} />
                        <Field label="Dex Armor" value={character?.dexArmor} />
                    </div>
                </div>
            </section>

            {/* Skills */}
            <section className="pcs-section">
                {/*<h2 className="pcs-h2">Skills</h2>*/}

                {grouped.length === 0 && <div className="pcs-empty">No skills on this character.</div>}

                {/* NEW: two-column wrapper */}
                <div className="pcs-skill-columns">
                    <div className="pcs-group" key="Knowledges">
                        <div className="pcs-group-title">Knowledges</div>
                        <table className="pcs-table">
                            <thead>
                            <tr>
                                <th style={{ width: "30%" }}>Ranks</th>
                                <th style={{ width: "70%" }}>Skill</th>
                            </tr>
                            </thead>
                            <tbody>
                            {character.knowledges.map((k) => {

                                return (
                                    <tr key={k.knowledgeId}>
                                        <td className="pcs-name pcs-right">
                                            <span className="pcs-ranks">{k.ranks}</span>
                                        </td>
                                        <td className="pcs-name">{k.name}</td>
                                        <td className="pcs-notes"></td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                    {grouped.map(({ key, label, items }) => (
                        <div className="pcs-group" key={key}>
                            <div className="pcs-group-title">{label}</div>
                            <table className="pcs-table">
                                <thead>
                                <tr>
                                    <th style={{ width: "30%" }}>Ranks</th>
                                    <th style={{ width: "70%" }}>Skill</th>
                                </tr>
                                </thead>
                                <tbody>
                                {items.map((r) => {
                                    let notes = r.description || r.shortDescription || "";
                                    if (typeof notes === "string") {
                                        const nl = notes.indexOf("\n");
                                        if (nl > -1) notes = notes.slice(0, nl);
                                    }
                                    return (
                                        <tr key={r.skillId || r.name}>
                                            <td className="pcs-name pcs-right">
                                                <span className="pcs-sp">{getSPCostString(r)}</span>
                                                <span className="pcs-ranks">
                                                    {r.incrementCost > 0 ? (r.ranks ?? "") : ""}
                                                </span>
                                            </td>

                                            <td className="pcs-name">{r.name || `#${r.skillId}`}</td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </section>
            {/* Armor Rep footer */}
            <section className="pcs-section pcs-armor-rep">
                <div className="pcs-armor-line">
                    <span className="pcs-armor-label">Armor Rep Values:</span>
                    <span className="pcs-armor-part">Torso <span className="pcs-armor-values">0 | 10 | 20 | 40</span></span>
                    <span className="pcs-armor-part">Arm <span className="pcs-armor-values">0 | 5 | 10 | 20</span></span>
                    <span className="pcs-armor-part">Leg <span className="pcs-armor-values">0 | 5 | 10 | 20</span></span>
                    <span className="pcs-head-blank pcs-armor-initials">Total</span>
                    <span className="pcs-head-blank pcs-armor-initials">Initials</span>
                </div>
            </section>
            {/* ---------- Skill Reference (new page) ---------- */}
            <div className="pcs-break" />
            <section className="pcs-section pcs-skill-ref">
                <h2 className="pcs-h2">Skill Reference</h2>
                <div className="pcs-ref-group">
                    {refSkills.map((s) => (
                        <div className="pcs-ref-item" key={`ref-static-${s.skillId || s.name}`}>
                            <div className="pcs-ref-name">{s.name}</div>
                            {/* Show description as its own block so we can control line-height */}
                            <div className="pcs-ref-desc">
                                {formatRefText(s.description)}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
